import {
    ChangeDetectionStrategy,
    Component,
    inject,
    type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { AuthService, SessionIdleService } from '@quipex/core/auth';
import { Splash } from '@quipex/core/layout';
import { MonitoringService } from '@quipex/core/logging';
import {
    injectViewportBreakpoints,
    UnloadWarningService,
} from '@quipex/core/utils';
import { FONT_SET_MAP, Loader, QpxHeroIcon } from '@quipex/shared/ui';
import { NgxSonnerToaster } from 'ngx-sonner';

@Component({
    selector: 'qpx-root',
    imports: [
        RouterOutlet,
        NgxSonnerToaster,
        QpxHeroIcon,
        Loader,
        MatIconModule,
        Splash,
    ],
    templateUrl: './app.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'h-full m-0',
        '[class.mobile]': '!isRailOrWider()',
        '(window:beforeunload)': 'handleBeforeUnload($event)',
        '[style.--qpx-padding-main.px]': 'isRailOrWider() ? 24 : 16',
    },
})
export class App implements OnInit {
    //#region Dependency injection
    private readonly authService = inject(AuthService);
    private readonly sessionIdleService = inject(SessionIdleService);
    private readonly monitoringService = inject(MonitoringService);
    private readonly iconRegistry = inject(MatIconRegistry);
    private readonly viewport = injectViewportBreakpoints();
    private readonly unloadService = inject(UnloadWarningService);
    //#endregion

    //#region State
    protected readonly isRailOrWider = this.viewport.atLeast('tablet768');
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        this.registerIconFontClassAlias();
        this.handleUnhandledErrors();
        this.manageIdleSession();
    }

    ngOnInit(): void {
        void this.monitoringService.initialize();
    }
    //#endregion

    //#region Template methods
    /* Alert the user on tab close/refresh that unwaited document upload is still in progress */
    protected handleBeforeUnload(event: BeforeUnloadEvent) {
        if (this.unloadService.shouldWarn()) {
            event.preventDefault();
        }
    }
    //#endregion

    //#region Private methods
    private handleUnhandledErrors(): void {
        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.monitoringService.logEvent(event.reason, {
                reason: event.reason?.message || 'Unknown promise rejection',
                url: window.location.href,
                timestamp: new Date().toISOString(),
            });
        });

        // Handle all other unhandled errors
        window.addEventListener('error', (event) => {
            this.monitoringService.logEvent(event.error, {
                message: event.message,
                filename: event.filename,
                lineNumber: event.lineno,
                columnNumber: event.colno,
                url: window.location.href,
                timestamp: new Date().toISOString(),
            });
        });
    }

    private manageIdleSession(): void {
        this.sessionIdleService.startIdleProcess();

        // When a user is idle for a set amount of time
        this.sessionIdleService.sessionIdled$
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.authService.logout());
    }

    private registerIconFontClassAlias(): void {
        for (const [alias, className] of Object.entries(FONT_SET_MAP)) {
            if (className) {
                this.iconRegistry.registerFontClassAlias(alias, className);

                if (className === FONT_SET_MAP['sym-outlined']) {
                    this.iconRegistry.setDefaultFontSetClass(className);
                }
            }
        }
    }
    //#endregion
}
